/**
 * DeliveryProofDialog - Dialog per conferma consegna con firma e foto
 */
import React, { useState, useRef, useEffect } from 'react';
import { getErrorMessage } from '@/lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Pen,
  Camera,
  Upload,
  Trash2,
  Check,
  Loader2,
  RotateCcw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DeliveryProofDialog = ({ open, onOpenChange, orderId, onSuccess }) => {
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('signature');

  // Reset quando si apre il dialog
  useEffect(() => {
    if (open) {
      setHasSignature(false);
      setPhotoFile(null);
      setPhotoPreview(null);
      setNotes('');
      setActiveTab('signature');
    }
  }, [open]);

  // Setup canvas per firma
  useEffect(() => {
    if (open && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, [open, activeTab]);

  // Gestione disegno firma
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Supporto touch e mouse
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    ctx.beginPath();
    ctx.moveTo(clientX - rect.left, clientY - rect.top);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    ctx.lineTo(clientX - rect.left, clientY - rect.top);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  // Gestione upload foto
  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine');
      return;
    }

    setPhotoFile(file);
    
    // Preview
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Submit
  const handleSubmit = async () => {
    if (!hasSignature && !photoFile) {
      toast.error('Aggiungi una firma o una foto come prova di consegna');
      return;
    }

    setSubmitting(true);
    try {
      // Se c'è la firma, invia come base64
      if (hasSignature) {
        const canvas = canvasRef.current;
        const signatureBase64 = canvas.toDataURL('image/png');
        
        await axios.post(`${API}/logistics/order/${orderId}/delivery-proof`, {
          signature_base64: signatureBase64,
          notes: notes || null
        });
      }

      // Se c'è la foto, upload separato
      if (photoFile) {
        const formData = new FormData();
        formData.append('file', photoFile);
        
        await axios.post(`${API}/logistics/order/${orderId}/delivery-proof/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      // Se solo note (senza firma/foto), invia comunque
      if (!hasSignature && !photoFile && notes) {
        await axios.post(`${API}/logistics/order/${orderId}/delivery-proof`, {
          notes: notes
        });
      }

      toast.success('Consegna confermata con successo!');
      onSuccess?.();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Errore nella conferma consegna'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Check className="h-5 w-5 text-green-600" />
            Conferma Consegna
          </DialogTitle>
          <DialogDescription>
            Raccogli una prova di avvenuta consegna (firma o foto)
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="signature" className="flex items-center gap-2">
              <Pen className="h-4 w-4" />
              Firma
            </TabsTrigger>
            <TabsTrigger value="photo" className="flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Foto
            </TabsTrigger>
          </TabsList>

          <TabsContent value="signature" className="space-y-4">
            <div className="space-y-2">
              <Label>Firma del destinatario</Label>
              <div className="relative border rounded-lg overflow-hidden bg-white">
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={200}
                  className="w-full touch-none cursor-crosshair"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  data-testid="signature-canvas"
                />
                {!hasSignature && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-muted-foreground">
                    <span className="text-sm">Firma qui</span>
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={clearCanvas}
                  disabled={!hasSignature}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Cancella
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="photo" className="space-y-4">
            <div className="space-y-2">
              <Label>Foto prova di consegna</Label>
              
              {photoPreview ? (
                <div className="relative">
                  <img 
                    src={photoPreview} 
                    alt="Prova di consegna" 
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2"
                    onClick={removePhoto}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div 
                  className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">
                    Clicca per caricare una foto
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    JPG, PNG (max 5MB)
                  </p>
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoSelect}
                className="hidden"
                data-testid="photo-upload-input"
              />
            </div>
          </TabsContent>
        </Tabs>

        {/* Note */}
        <div className="space-y-2">
          <Label>Note (opzionale)</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Es: Consegnato al portiere, pacco lasciato in reception..."
            rows={2}
            data-testid="delivery-notes-input"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={submitting || (!hasSignature && !photoFile)}
            className="bg-green-600 hover:bg-green-700"
            data-testid="confirm-delivery-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Conferma...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Conferma Consegna
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeliveryProofDialog;
